import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslate } from "react-admin";
import { useQuery } from "react-query";

import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from "@mui/x-data-grid";
import { FeatureCode } from "@wallet-manager/node-types";

import {
  getCrossMerchantTransferRecord,
  getCrossMerchantTransferRecordExport,
} from "../../../api/merchant";
import { CustomPagination } from "../../../components/CustomPagination";
import LoadingDialog from "../../../components/LoadingDialog";
import { NoRowsOverlay } from "../../../components/NoRowsOverlay";
import { useAlerting, usePermission, useTranslation } from "../../../hooks";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../hooks/useColumnHide";
import { dataGridDefaults } from "../../../utils/constant";
import { copyToClipboard } from "../../../utils/helper";
import { getFilteredColumns, useGenGridCol } from "../../../utils/HelperComp";
import { useZusParams } from "./CrossMerchantTransferTransactionRecord";
import { useAppSelector } from "../../../reducer/hooks";
import { selectCrossMerchant } from "../../../reducer/crossMerchantSlice";

const Pkey = FeatureCode.FeaturesLedger.CrossMerchantTransferTransactionRecord;

const translatePrefix = "crossMerchantTransferTransactionRecord";
const api = {
  getCrossMerchantTransferRecord,
  getCrossMerchantTransferRecordExport,
};

export default function CrossMerchantTransferTransactionRecordList(props: {
  listMapping: (
    which: "key" | "name",
    array: any[],
    config?: {
      omitKeys?: string[];
      t: any;
      te: any;
      merchantMapping?: Record<string, string | number>;
    }
  ) => any[][];
  setCount: Dispatch<SetStateAction<number>>;
}) {
  const { t, te } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const { merchantMapping } = useAppSelector(selectCrossMerchant);
  const { listMapping, setCount } = props;
  const listRes = useQuery({
    queryKey: [
      "crossMerchantTransferTransactionRecord",
      zusParams.body,
      zusParams.refetchCounter,
    ],
    queryFn: () => api.getCrossMerchantTransferRecord(zusParams.body),
  });
  const { rows = [], count = 0 } =
    (listRes.data as any as { rows: any[]; count: number }) || {};
  const content: GridRowsProp = listMapping("key", rows, {
    omitKeys: [],
    t,
    te,
    merchantMapping,
  });

  useEffect(() => setCount(count), [listRes]);

  const columns: GridColDef[] = [
    useGenGridCol("ledger_transaction_id", t("ledger_transaction_id"), {
      minWidth: 200,
    }),
    useGenGridCol("original_order_id", t("original_order_id"), {
      minWidth: 150,
    }),
    useGenGridCol("merchant_order_id", t("merchant_order_id"), {
      minWidth: 300,
    }),
    useGenGridCol("merchant_lp", t("merchant_lp"), {
      minWidth: 200,
    }),
    useGenGridCol("liquidity_providers", t("liquidity_providers"), {
      minWidth: 150,
    }),
    useGenGridCol("merchant_id", t("merchant_id"), { minWidth: 150 }),
    useGenGridCol("client_id", t("client_id"), { minWidth: 150 }),
    useGenGridCol("sending_merchant_id", t("sending_merchant_id"), {
      minWidth: 150,
    }),
    useGenGridCol("sending_client_id", t("sending_client_id"), {
      minWidth: 150,
    }),
    useGenGridCol("from_client_id", t("from_client_id"), { minWidth: 150 }),
    useGenGridCol("receiving_merchant_id", t("receiving_merchant_id"), {
      minWidth: 150,
    }),
    useGenGridCol("receiving_client_id", t("receiving_client_id"), {
      minWidth: 150,
    }),
    useGenGridCol("to_client_id", t("to_client_id"), { minWidth: 150 }),
    useGenGridCol("transaction_type", t("transaction_type"), { minWidth: 150 }),
    useGenGridCol("direction", t("direction"), { minWidth: 200 }),
    useGenGridCol(
      "cross_merchant_transfer_direction",
      t("cross_merchant_transfer_direction"),
      { minWidth: 200 }
    ),
    useGenGridCol("chain_name", t("chain_name"), { minWidth: 150 }),
    useGenGridCol("currency", t("currency")),
    useGenGridCol("amount", t("amount"), { minWidth: 150 }),
    useGenGridCol("post_balance", t("post_balance"), { minWidth: 150 }),
    useGenGridCol("freeze_amount", t("freeze_amount"), { minWidth: 150 }),
    useGenGridCol("post_freeze_balance", t("post_freeze_balance"), {
      minWidth: 150,
    }),
    useGenGridCol("created_at", t("created_at"), { minWidth: 150 }),
    useGenGridCol("remarks", t("remarks"), { minWidth: 450 }),
  ];
  const { filteredColumns } = getFilteredColumns(columns, []);
  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    filteredColumns,
    getColumnHideConfig()
  );
  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;
  return (
    <>
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        rowCount={count}
        columns={filteredColumns}
        page={zusParams.body.page}
        onPageChange={zusParams.setPage}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}
